import React, { useState, useEffect, useContext } from "react";
import {
  Trophy,
  Medal,
  Download,
  CheckCircle2,
  Calendar,
  Award,
  User,
  Mail,
} from "lucide-react";
import TopbarNonav from "./components/TopbarNonav";
import { AuthContext } from "./context/AuthContext";

const CourseCertificate = ({
  userName,
  courseName = "SQL Guroo",
  completionDate,
  score,
  totalQuestions,
}) => {
  const { firstName, lastName, username } = useContext(AuthContext);
  const [certificateDetails, setCertificateDetails] = useState({
    grade: "",
    color: "",
    gradient: "",
    borderColor: "",
  });

  const [isDownloading, setIsDownloading] = useState(false);

  useEffect(() => {
    // Determine grade and styling based on performance
    const calculateGrade = () => {
      const percentageScore = (score / totalQuestions) * 100;

      if (percentageScore >= 90) {
        setCertificateDetails({
          grade: "Platinum",
          color: "text-gray-100",
          gradient: "from-gray-700 via-gray-800 to-gray-900",
          borderColor: "border-gray-500",
        });
      } else if (percentageScore >= 80) {
        setCertificateDetails({
          grade: "Gold",
          color: "text-yellow-50",
          gradient: "from-yellow-500 via-yellow-600 to-yellow-700",
          borderColor: "border-yellow-400",
        });
      } else if (percentageScore >= 70) {
        setCertificateDetails({
          grade: "Silver",
          color: "text-gray-50",
          gradient: "from-gray-300 via-gray-400 to-gray-500",
          borderColor: "border-gray-300",
        });
      } else {
        setCertificateDetails({
          grade: "Bronze",
          color: "text-orange-50",
          gradient: "from-orange-500 via-orange-600 to-orange-700",
          borderColor: "border-orange-400",
        });
      }
    };

    calculateGrade();
  }, [score, totalQuestions]);

  const downloadCertificate = async () => {
    try {
      setIsDownloading(true);
      const response = await fetch(
        "https://sqlguroo.com/subapi/generate-certificate",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            userName: `${firstName} ${lastName}`,
            userEmail: username,
            courseName,
            completionDate,
            score,
            totalQuestions,
            grade: certificateDetails.grade,
          }),
        }
      );

      if (!response.ok) {
        throw new Error("Certificate generation failed");
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${firstName}_${lastName}_SQL_Guroo_Certificate.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error("Error downloading certificate:", error);
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <TopbarNonav />

      <div className="max-w-4xl mx-auto px-4 py-8 sm:px-6 lg:px-8">
        <div
          className={`relative overflow-hidden rounded-2xl shadow-2xl bg-gradient-to-br ${certificateDetails.gradient} ${certificateDetails.color}`}
        >
          {/* Decorative elements */}
          <div className="absolute top-0 left-0 w-full h-full opacity-10">
            <div className="absolute top-10 left-10 w-32 h-32 border-2 border-current rounded-full" />
            <div className="absolute bottom-10 right-10 w-32 h-32 border-2 border-current rounded-full" />
          </div>

          <div className="relative p-8 sm:p-12">
            {/* Header */}
            <div className="text-center mb-8">
              <div className="inline-block mb-4 transform hover:scale-105 transition-transform duration-300">
                <Trophy size={80} className="text-yellow-300" />
              </div>
              <h1 className="text-4xl font-bold mb-2">
                Certificate of Achievement
              </h1>
              <p className="text-lg opacity-90">
                SQL Guroo Certification Program
              </p>
            </div>

            {/* User Information Section */}
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-8 mb-8">
              <div className="text-center mb-8">
                <div className="flex items-center justify-center space-x-4 mb-4">
                  <User className="w-8 h-8 text-yellow-300" />
                  <h2 className="text-3xl font-bold">
                    {firstName} {lastName}
                  </h2>
                </div>
                <div className="flex items-center justify-center space-x-2 mb-4">
                  <Mail className="w-5 h-5 text-yellow-300" />
                  <p className="text-lg opacity-90">{username}</p>
                </div>
                <div className="border-t border-white/20 pt-4">
                  <p className="text-xl">Has successfully completed</p>
                  <p className="text-2xl font-semibold text-yellow-300">
                    {courseName}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
                <div className="flex items-center space-x-4">
                  <Calendar className="w-8 h-8 text-yellow-300" />
                  <div>
                    <p className="text-sm opacity-80">Completion Date</p>
                    <p className="font-semibold">
                      {completionDate || new Date().toLocaleDateString()}
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-4">
                  <CheckCircle2 className="w-8 h-8 text-yellow-300" />
                  <div>
                    <p className="text-sm opacity-80">Performance</p>
                    <p className="font-semibold">
                      {score} / {totalQuestions} Questions
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-center space-x-3 mb-8">
                <Award className="w-8 h-8 text-yellow-300" />
                <span className="text-2xl font-bold">
                  {certificateDetails.grade} Level Achievement
                </span>
              </div>
            </div>

            {/* Download button */}
            <div className="text-center">
              <button
                onClick={downloadCertificate}
                disabled={isDownloading}
                className={`
                  inline-flex items-center px-8 py-3 rounded-full
                  bg-white text-gray-900 font-semibold
                  hover:bg-gray-100 transition-all duration-200
                  shadow-lg hover:shadow-xl
                  disabled:opacity-50 disabled:cursor-not-allowed
                  transform hover:scale-105 active:scale-95
                `}
              >
                {isDownloading ? (
                  <>
                    <svg
                      className="animate-spin -ml-1 mr-3 h-5 w-5 text-gray-900"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Generating...
                  </>
                ) : (
                  <>
                    <Download className="w-5 h-5 mr-2" />
                    Download Certificate
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Footer */}
          <div className="bg-black/20 p-4 text-center text-sm">
            <p>
              This certificate is digitally generated and can be verified at
              sqlguroo.com/verify
            </p>
            <p className="mt-1 text-xs opacity-75">
              Certificate ID: {Math.random().toString(36).substring(2, 15)}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CourseCertificate;
